import { DownOutlined } from '@ant-design/icons';
import { Dropdown, Image, Spin } from 'antd';
import { Check, Network, Wallet } from 'lucide-react';
import { useState } from 'react';
import { Card } from 'src/components/ui/card';
import { useAppTranslation } from 'src/i18n/hooks';
import { THEME_MODE } from 'src/interfaces/theme';
import { useAppSelector } from 'src/store';
import { getChainInfo } from 'src/utils/chainHelpers';
import '../../../../pages/exchange/components/styles/withdraw-modal.scss';

interface GroupedChainData {
  chain_id: number;
  chain_name: string;
  connected: boolean;
}

type ListFundsDropdownProps = {
  customClassName?: string;
  chainData: GroupedChainData[];
  onSelectChain: (chainId: number) => void;
};

const NetworkSelect = ({ chainData, onSelectChain }: ListFundsDropdownProps) => {
  const [open, setOpen] = useState(false);
  const isPending = false;
  const { t } = useAppTranslation(['notifications', 'trade']);
  const userTheme = useAppSelector((state: any) => state.theme);
  const isDarkMode = userTheme === THEME_MODE.DARK;

  const currentNetwork = chainData.find((chain) => chain.connected);

  const menu = (
    <div className="rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 min-w-[320px] network-dropdown-menu">
      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Network className="w-4 h-4 text-blue-500" />
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {t('trade:withdrawModal.availableNetworks')}
          </span>
        </div>
      </div>

      {isPending ? (
        <div className="flex justify-center items-center h-20">
          <div className="flex items-center gap-2">
            <Spin size="small" />
            <span className="text-sm text-gray-500">
              {t('trade:withdrawModal.switchingNetwork')}
            </span>
          </div>
        </div>
      ) : (
        <div className="space-y-2 p-2 max-h-80 overflow-y-auto">
          {chainData.map((chain, index) => (
            <Card
              key={index}
              className={`cursor-pointer transition-all duration-200 hover:shadow-md border network-option-card ${
                chain.connected
                  ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                  : 'border-gray-200 hover:border-blue-300 dark:border-gray-700 dark:hover:border-blue-600'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onSelectChain(chain.chain_id);
                setOpen(false);
              }}
            >
              <div className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative flex items-center justify-center">
                      <Image
                        src={getChainInfo(chain.chain_id).logo}
                        alt="network-logo"
                        width={28}
                        height={28}
                        preview={false}
                        style={{ borderRadius: '50%' }}
                        className="border-2 border-white shadow-sm"
                      />
                      {chain.connected && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {chain.chain_name}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}

          {chainData.length === 0 && (
            <div className="text-center py-8">
              <Wallet className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('trade:withdrawModal.noNetworksAvailable')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className={'w-full'}>
      <Dropdown
        open={open}
        overlay={menu}
        trigger={['click']}
        placement="bottomLeft"
        overlayClassName={`bg-[white] rounded-lg network-dropdown ${isDarkMode ? 'network-dropdown-dark' : ''}`}
        onOpenChange={() => setOpen(false)}
      >
        <Card
          className="w-full dark:bg-transparent cursor-pointer transition-all duration-200 border-[#494950] border-[1px] network-selector-card"
          onClick={() => setOpen(true)}
        >
          <div className="p-[10px]">
            <div className="flex items-center justify-between">
              <div className="flex justify-center items-center gap-3">
                <div className="relative flex items-center justify-center">
                  {currentNetwork ? (
                    <>
                      <Image
                        src={getChainInfo(currentNetwork.chain_id).logo}
                        alt="current-network"
                        width={32}
                        height={32}
                        preview={false}
                        style={{ borderRadius: '50%' }}
                        className="border-2 border-white shadow-sm"
                      />
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                    </>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-green-500 flex items-center justify-center">
                      <Network className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
                <div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {currentNetwork
                      ? currentNetwork.chain_name
                      : t('trade:withdrawModal.selectNetwork')}
                  </span>
                </div>
              </div>
              <DownOutlined
                className={`text-blue-500 transition-transform duration-200 ${
                  open ? 'rotate-180' : ''
                }`}
              />
            </div>
          </div>
        </Card>
      </Dropdown>
    </div>
  );
};

export default NetworkSelect;
